import axios from 'axios';
import { atom, useAtom, type Atom } from 'jotai';

import { getConfig } from '../../../../../config';
import { JSON_CONTENT_TYPE } from '../../../../state/graphqlEndpoints';
import type { DataChangeStreams } from '../../../graph-explorer/home/ObjectsVisualizer/RelationshipTypes';

// Types
export interface RelationshipAlias {
	name: string;
	isLatest: boolean;
	deprecated: boolean;
	inverse: boolean;
	isAliasExternallyAvailable: boolean;
	isHiddenFromGraphQLSchema: boolean;
}

export interface Relationship {
	fromType: string[];
	targetType: string[];
	ownerInfo: {
		pointOfContact: string;
		team: string;
		opsgenieTeamId: string;
	};
	permissionCheck: Record<string, any>;
	hidden: boolean;
	availableIn: string[];
	sendsAnalytics: string;
	sendsToFlock: boolean;
	category: string;
	id: string;
	name: string;
	version: string;
	deleted: boolean;
	fromTypeName: string[];
	targetTypeName: string[];
	providerType: {
		source: string[];
		target: string[];
	};
	isRelationshipExternallyAvailable?: boolean;
	aliases?: RelationshipAlias[];
	/**
	 * Stored-metadata definitions. The jq programs under the
	 * `createEvents[]` / `updateEvents[]` / `objectUpdateRecord[]` entries
	 * (`openSearchObjectMetadata`) describe the OpenSearch-indexed fields a
	 * Cypher `WHERE` can filter on. Optional because not every source populates
	 * it (e.g. the legacy registered-types.json endpoint).
	 */
	dataChangeStreams?: DataChangeStreams;
}

// Always use the direct URL - CORS should be handled by browser extension or server config
const getRelationshipsEndpoint = () => {
	return getConfig().graphStoreUrl + '/api/relationship-types/registered-types.json';
};

export const relationshipsAsyncAtom: Atom<Promise<Record<string, Relationship>>> = atom(
	async () => {
		const endpoint = getRelationshipsEndpoint();
		const response = await axios.get<Record<string, Relationship>>(endpoint, {
			headers: {
				'Content-Type': JSON_CONTENT_TYPE,
			},
			withCredentials: true,
		});
		return response.data;
	},
);

export function useRelationships(): {
	[k: string]: Relationship;
} {
	const [value] = useAtom(relationshipsAsyncAtom);

	if (!value) {
		return {};
	}

	// Filter out relationships with hidden = true
	const filteredRelationships = Object.fromEntries(
		Object.entries(value).filter(([_, relationship]) => !relationship.hidden),
	);

	return filteredRelationships;
}
